/*
 * Transient notifications — the only notification pattern in the storefront.
 *
 * Anything can `push()` (add to cart, wishlist, undoable removals, copy);
 * `ToastHost.vue` renders the stack once, in `App.vue`. A toast auto-dismisses
 * after `ms`; hovering it holds the clock. `action` is either a link (`to`)
 * or a button (`onClick`) — undo lives in the button form.
 */
import { defineStore } from 'pinia';

export interface ToastAction {
  label: string;
  to?: string;
  onClick?: () => void;
}

export interface Toast {
  id: number;
  title: string;
  body?: string;
  /** Cover thumbnail, for product-related toasts. */
  image?: string | null;
  tone: 'success' | 'error' | 'info';
  action?: ToastAction;
}

export type ToastInput = Omit<Toast, 'id' | 'tone'> & { tone?: Toast['tone'] };

const DEFAULT_MS = 3800;
/** Undo toasts stay up longer so the reader can reach the button. */
export const UNDO_MS = 6000;
/** Visible stack cap — pushing one more drops the oldest. */
export const MAX_VISIBLE = 3;

let nextId = 0;
// Timers are bookkeeping, not UI state — kept out of the reactive store.
const timers = new Map<number, { handle: number; ms: number }>();

export const useToastStore = defineStore('toast', {
  state: () => ({ items: [] as Toast[] }),
  actions: {
    push(input: ToastInput, ms = DEFAULT_MS): number {
      const id = ++nextId;
      this.items.push({ tone: 'success', ...input, id });
      while (this.items.length > MAX_VISIBLE) this.dismiss(this.items[0].id);
      timers.set(id, { handle: window.setTimeout(() => this.dismiss(id), ms), ms });
      return id;
    },
    dismiss(id: number) {
      const timer = timers.get(id);
      if (timer) window.clearTimeout(timer.handle);
      timers.delete(id);
      this.items = this.items.filter((t) => t.id !== id);
    },
    /** Hover / focus hold. */
    hold(id: number) {
      const timer = timers.get(id);
      if (timer) window.clearTimeout(timer.handle);
    },
    /** Re-arm with the full duration so the reader gets a fresh window. */
    release(id: number) {
      const timer = timers.get(id);
      if (timer) timer.handle = window.setTimeout(() => this.dismiss(id), timer.ms);
    },
  },
});
